import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Title } from 'refactored/ui/typography';

import { constants as dateAndTimeConstants } from 'utils/dateAndTimes';
import Link from 'components/styled/link';

import StopAddress from 'components/form/StopAddress';
import StopAddressWhenEdit from 'components/form/StopAddressWhenEdit';
import { AddressFormModal } from 'components/AddressModal/container';
import DatePicker from 'components/form/DatePicker';
import RelativeDatePicker from 'components/form/RelativeDatePicker';
import TimePickers from 'components/form/TimePickers';
import HandlingExtra from './HandlingExtra';
import { RemoveButton } from 'refactored/ui/form';
import { constants as extrasConstants } from 'utils/extras';
import { useTranslation } from 'react-i18next';
import { selectors as uiSelectors } from 'state/ui';
import { selectors as settingsSelectors } from 'state/settings';
import {
  actions as orderActions,
  selectors as orderSelectors,
} from 'state/order';
import {
  actions as shipmentsActions,
  selectors as shipmentsSelectors,
} from 'state/shipments';
import { useDispatch, useSelector } from 'react-redux';
import orderConstants from 'utils/order/constants';
import { appointmentRequired } from 'utils/order';
import { compareObjects } from 'utils/object';
import LoadsForm from 'refactored/features/order/loads/form/loadsForm';
import loadSelectors from 'state/loads/selectors';
import { isEmpty } from 'utils/types';
import LoadsSummaryInline from 'refactored/features/order/loads/displays/LoadsSummaryInline';

const handlingExtras = extrasConstants.HANDLING_EXTRAS;

const MemoHandlingExtra = React.memo(HandlingExtra);

const Stop = ({
  className,
  stopType,
  shipmentKey,
  handleModalClose,
  position,
  isMultiRoute,
  canDelete,
  loadsInForm,
  typeOfOrder,
}) => {
  const { t } = useTranslation('Stop');
  const dispatch = useDispatch();
  const isCreating = useSelector((state) =>
    uiSelectors.addressModalIsOpened(state, shipmentKey, stopType)
  );
  const extras = useSelector(settingsSelectors.getCountrySettings)?.extras;
  const isRecurring = useSelector(orderSelectors.getIsRecurringOrder);
  const isEditing = useSelector(orderSelectors.getIsEditing);
  const orderType = useSelector(orderSelectors.getTypeOfOrder);
  const address = useSelector(
    (state) =>
      shipmentsSelectors.getShipmentAddress(state, shipmentKey, stopType),
    compareObjects
  );

  const loads = useSelector(
    (state) => loadSelectors.getLoadsFilled(state, shipmentKey),
    compareObjects
  );

  const loadsToUse = !isEmpty(loadsInForm) ? loadsInForm : loads;

  const handleRemoveShipment = useCallback(() => {
    dispatch(shipmentsActions.deleteShipment({ shipmentKey, stopType }));
  }, [dispatch, shipmentKey, stopType]);

  const handleChangeToDirect = useCallback(() => {
    dispatch(orderActions.setTypeOrder({ type: orderConstants.DIRECT_ORDER }));
  }, [dispatch]);

  const title = t(`title_${stopType}`);
  const hasHandlingExtra = extras?.some((e) => handlingExtras.indexOf(e) >= 0);
  const withDate = !isRecurring;
  const withRelativeDate =
    isRecurring && stopType === dateAndTimeConstants.STOP_TYPES.dropoff;
  const showAppointmentInfo = appointmentRequired({
    stopType,
    orderType,
    poi_info: address?.poi_info,
  });

  return (
    <>
      <div className={`${className} stop`} data-testid="stop">
        <div className="grid">
          <div className="grid header">
            <div className="title">
              <Title level="h3">
                {`${title} ${isMultiRoute ? position : ''}`}
              </Title>
            </div>
            {isMultiRoute && canDelete && !isEditing && (
              <RemoveButton handleRemove={handleRemoveShipment} />
            )}
          </div>
          <div className="field address first">
            {!isEditing ? (
              <StopAddress
                key={`${orderType}-${shipmentKey}-${stopType}`}
                shipmentKey={shipmentKey}
                stopType={stopType}
              />
            ) : (
              <StopAddressWhenEdit
                shipmentKey={shipmentKey}
                stopType={stopType}
              />
            )}
          </div>

          {!showAppointmentInfo && (
            <>
              {withDate && (
                <div className="field second" data-testid="stop_with-date">
                  <DatePicker shipmentKey={shipmentKey} stopType={stopType} />
                </div>
              )}

              {withRelativeDate && (
                <div className="field second" data-testid="stop_relative-date">
                  <RelativeDatePicker
                    shipmentKey={shipmentKey}
                    stopType={stopType}
                  />
                </div>
              )}

              <div className="field last">
                <TimePickers
                  shipmentKey={shipmentKey}
                  stopType={stopType}
                  typeOfOrder={typeOfOrder}
                />
              </div>
            </>
          )}
          {showAppointmentInfo && (
            <div className="appointment" data-testid="stop_appointment-info">
              <span>{t('appointment_info')}</span>
              <span className="link">
                <Link
                  text={t('appointment_link')}
                  onClick={handleChangeToDirect}
                />
              </span>
            </div>
          )}
        </div>

        <div className="extraInfo">
          {hasHandlingExtra && (
            // use orderType, shipmentKey and StopType as key so Handling extra is reseted
            <MemoHandlingExtra
              key={`${orderType}-${shipmentKey}-${stopType}`}
              stopType={stopType}
              shipmentKey={shipmentKey}
            />
          )}

          <div className="palletInfo">
            <LoadsSummaryInline
              stopType={dateAndTimeConstants.STOP_TYPES[stopType]}
              loads={loadsToUse}
            />
          </div>
        </div>

        {isCreating && (
          <AddressFormModal
            shipmentKey={shipmentKey}
            stopType={stopType}
            onClose={handleModalClose}
          />
        )}
      </div>

      {isMultiRoute && <LoadsForm shipmentKey={shipmentKey} isMultiRoute />}
    </>
  );
};

Stop.propTypes = {
  shipmentKey: PropTypes.string.isRequired,
  className: PropTypes.string,
  stopType: PropTypes.oneOf(['pickup', 'dropoff']).isRequired, // TODO  this valus should came from config file
  handleModalClose: PropTypes.func,
  isCreating: PropTypes.bool,
  title: PropTypes.string,
  hasHandlingExtra: PropTypes.bool,
  loads: PropTypes.arrayOf(PropTypes.shape({})),
  position: PropTypes.number,
  isMultiRoute: PropTypes.bool,
  canDelete: PropTypes.bool,
  loadsInForm: PropTypes.arrayOf(PropTypes.shape({})),
  handleRemoveShipment: PropTypes.func,
  typeOfOrder: PropTypes.string,
  withDate: PropTypes.bool,
  withRelativeDate: PropTypes.bool,
  isEditing: PropTypes.bool,
};

Stop.defaultProps = {
  className: '',
  isCreating: false,
  title: '',
  hasHandlingExtra: false,
  handleModalClose: undefined,
  loads: [],
  loadsInForm: [],
  position: 0,
  isMultiRoute: false,
  canDelete: false,
  handleRemoveShipment: () => ({}),
  typeOfOrder: '',
  withDate: true,
  withRelativeDate: false,
  isEditing: false,
};

export default Stop;
